/**
 * InPageTopBar — the slim top bar that sits ABOVE every logged-in
 * page now that the global navigation lives in the left rail.
 *
 * Page-level affordances:
 *   - Mobile hamburger (opens the rail drawer; hidden on desktop)
 *   - Page title (optional — supplied by the page via `title` prop)
 *   - Recently-visited lesson pills (Phase 115.7 — surfaces the last
 *     few lessons the teacher opened so they can jump back without
 *     going through My Library every time)
 *   - Marketing nav: Skills Library / Why Skills / The Path
 *   - A single primary action slot (optional — `actions` prop)
 *
 * Account, sign-out, and global product nav are NOT here — those
 * live in the left rail.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, ChevronDown, Clock, Compass, HelpCircle, Menu, Radio } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { listActivities, fetchCommunityRecentTicker } from "../../lib/api";
import { RECENT_LESSONS_EVENT } from "../../lib/recentLessons";
import { formatRelativeTime } from "../../lib/relativeTime";

const _linkClass =
  "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-bold text-[#4a6984] hover:text-[#1c2d3d] hover:bg-[#f4f5f7] no-underline transition-colors";

// Phase 124.B (revised) — the "Recent" dropdown ALWAYS renders in the
// top bar so it's a discoverable, fixed navigation affordance. It
// surfaces lessons CREATED in the last 7 days (not lessons opened),
// capped at 7 items newest-first. Empty state reads "No recent lessons
// created" so the user understands why the list is blank.
const RECENT_WINDOW_DAYS = 7;
const RECENT_MAX_ITEMS = 7;

const _isWithinLast7Days = (createdAt) => {
  if (!createdAt) return false;
  const ts =
    typeof createdAt === "string"
      ? Date.parse(createdAt)
      : createdAt instanceof Date
        ? createdAt.getTime()
        : Number(createdAt);
  if (!Number.isFinite(ts)) return false;
  const cutoff = Date.now() - RECENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return ts >= cutoff;
};

const RecentLessonsDropdown = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    const load = async () => {
      try {
        const docs = await listActivities();
        if (abort) return;
        const filtered = (Array.isArray(docs) ? docs : [])
          .filter((d) => d && d.id && _isWithinLast7Days(d.created_at))
          .slice(0, RECENT_MAX_ITEMS)
          .map((d) => ({
            id: d.id,
            name: d.activity_name || "Untitled lesson",
            output_type: d.output_type || "lesson",
            created_at: d.created_at,
          }));
        setItems(filtered);
      } catch {
        if (!abort) setItems([]);
      } finally {
        if (!abort) setLoading(false);
      }
    };
    load();
    // Refresh when a sibling component signals an activity change
    // (e.g. after a new lesson is generated) or when the tab regains
    // focus so the list stays accurate without polling.
    const refresh = () => load();
    window.addEventListener(RECENT_LESSONS_EVENT, refresh);
    window.addEventListener("focus", refresh);
    return () => {
      abort = true;
      window.removeEventListener(RECENT_LESSONS_EVENT, refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const count = items.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="top-bar-recent-dropdown-trigger"
          aria-label="Recent lessons created in the last 7 days"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-bold text-[#4a6984] hover:text-[#1c2d3d] hover:bg-[#f4f5f7] border border-[#4a6984]/20 transition-colors"
        >
          <Clock className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Recent</span>
          {count > 0 ? (
            <span
              data-testid="top-bar-recent-count"
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#96ded1] text-[#1c2d3d] text-[10px] font-extrabold leading-none"
            >
              {count}
            </span>
          ) : null}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="top-bar-recent-dropdown-content"
        align="start"
        sideOffset={6}
        className="w-72 max-w-[90vw]"
      >
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
          Created in the last 7 days
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading ? (
          <div
            data-testid="top-bar-recent-loading"
            className="px-2 py-3 text-xs text-[#4a6984]/70"
          >
            Loading…
          </div>
        ) : count === 0 ? (
          <div
            data-testid="top-bar-recent-empty"
            className="px-2 py-3 text-xs text-[#4a6984]/70"
          >
            No recent lessons created.
          </div>
        ) : (
          items.map((entry) => (
            <DropdownMenuItem
              key={entry.id}
              asChild
              data-testid={`top-bar-recent-${entry.id}`}
              className="cursor-pointer"
            >
              <Link
                to={`/lesson/${entry.id}`}
                title={entry.name}
                className="flex items-center justify-between gap-3 py-1.5 no-underline"
              >
                <span className="truncate text-sm font-semibold text-[#1c2d3d]">
                  {entry.name}
                </span>
                <span
                  data-testid={`top-bar-recent-${entry.id}-time`}
                  className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#4a6984]/70"
                >
                  {formatRelativeTime(entry.created_at)}
                </span>
              </Link>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Phase 124.C — live "Community Pulse" ticker. Rotates every 5s
// through the latest community-shared lessons so teachers can see the
// platform is alive and what their peers are publishing. Hidden on
// small screens to keep the bar uncluttered. Clicks through to the
// Community library tab.
const TICKER_ROTATE_MS = 5000;
const TICKER_REFRESH_MS = 90_000;

const CommunityPulseTicker = () => {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const data = await fetchCommunityRecentTicker(10);
        if (!mountedRef.current) return;
        const next = Array.isArray(data?.items) ? data.items : [];
        setItems(next);
        setIndex((prev) => (next.length === 0 || prev >= next.length ? 0 : prev));
      } catch {
        // Auth gates etc. — silently no-op so the rest of the bar still renders.
        if (mountedRef.current) setItems([]);
      }
    };
    load();
    const refreshTimer = window.setInterval(load, TICKER_REFRESH_MS);
    return () => {
      mountedRef.current = false;
      window.clearInterval(refreshTimer);
    };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const rotate = window.setInterval(() => {
      setFade(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % items.length);
        setFade(true);
      }, 220);
    }, TICKER_ROTATE_MS);
    return () => window.clearInterval(rotate);
  }, [items.length]);

  if (!items || items.length === 0) return null;
  const item = items[index] || items[0];
  const when = formatRelativeTime(item.shared_at);

  return (
    <Link
      to="/dashboard?view=community"
      data-testid="top-bar-community-pulse"
      title={`${item.display_name} shared "${item.activity_name}"`}
      className="hidden lg:inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#1c2d3d] text-white/95 no-underline hover:bg-[#1c2d3d]/90 transition-colors max-w-[440px] min-w-0"
    >
      <span className="relative inline-flex h-2 w-2 shrink-0" aria-hidden>
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#96ded1] opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#96ded1]" />
      </span>
      <Radio className="h-3.5 w-3.5 shrink-0 text-[#96ded1]" />
      <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#96ded1] shrink-0">
        Pulse
      </span>
      <span
        data-testid="top-bar-community-pulse-text"
        className={`text-[12px] font-semibold truncate transition-opacity duration-200 ${fade ? "opacity-100" : "opacity-0"}`}
      >
        <span className="text-white/70">{item.display_name}</span>
        <span className="px-1.5 text-white/40">·</span>
        <span className="text-white">{item.activity_name}</span>
      </span>
      {when ? (
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-white/60">
          {when}
        </span>
      ) : null}
    </Link>
  );
};


export const InPageTopBar = ({
  title,
  subtitle,
  actions,
  onOpenMobileRail,
}) => {
  return (
    <header
      data-testid="in-page-top-bar"
      className="sticky top-0 z-20 flex items-center gap-3 h-14 px-4 sm:px-6 bg-white/95 backdrop-blur border-b border-[#4a6984]/15"
    >
      <button
        type="button"
        onClick={onOpenMobileRail}
        className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-md text-[#1c2d3d] hover:bg-[#f4f5f7] border border-[#4a6984]/20"
        aria-label="Open navigation"
        data-testid="top-bar-mobile-nav-trigger"
      >
        <Menu className="h-5 w-5" />
      </button>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0 shrink">
          {title ? (
            <p
              className="font-black text-sm sm:text-base text-[#1c2d3d] truncate"
              data-testid="top-bar-title"
            >
              {title}
            </p>
          ) : null}
          {subtitle ? (
            <p className="text-[11px] text-[#4a6984]/75 truncate -mt-0.5">
              {subtitle}
            </p>
          ) : null}
        </div>
        <RecentLessonsDropdown />
        <CommunityPulseTicker />
      </div>

      {/* Brand nav — Skills Library / Why Skills / The Path. */}
      <nav
        aria-label="Brand"
        data-testid="top-bar-brand-nav"
        className="flex items-center gap-0.5 shrink-0"
      >
        <Link
          to="/skills"
          data-testid="top-bar-skills"
          className={`${_linkClass} hidden md:inline-flex`}
        >
          <BookOpenText className="h-3.5 w-3.5" /> Skills Library
        </Link>
        <Link
          to="/why-skills"
          data-testid="top-bar-why-skills"
          className={`${_linkClass} hidden md:inline-flex`}
          aria-label="Why skills?"
        >
          <HelpCircle className="h-3.5 w-3.5" /> Why skills?
        </Link>
        <Link
          to="/the-path"
          data-testid="top-bar-the-path"
          className={`${_linkClass} hidden md:inline-flex`}
        >
          <Compass className="h-3.5 w-3.5" /> The Path
        </Link>

        {/* Mobile-only Why Skills entry. */}
        <Link
          to="/why-skills"
          data-testid="top-bar-why-skills-mobile"
          className={`${_linkClass} md:hidden`}
          aria-label="Why skills?"
        >
          <HelpCircle className="h-4 w-4" />
        </Link>
      </nav>

      {actions ? (
        <div className="flex items-center gap-2 shrink-0 pl-2 border-l border-[#4a6984]/15" data-testid="top-bar-actions">
          {actions}
        </div>
      ) : null}
    </header>
  );
};

export default InPageTopBar;
